import {
  HOST_QUERY_KEY,
  iamApiInstance,
  type ServiceAppState,
  TOKEN_SCOPE,
} from '@kc-console/context';
import { STATUS_CODE } from '@kc-console/core';

import { commonGuardErrorHandler } from '../../utils/commonGuardErrorHandler';
import { getClientIp } from '../../utils/getClientIp';

import type { AxiosError } from 'axios';
import type { Guard } from '../../types';

/**
 * 도메인 토큰을 생성하는 가드
 * @description
 * - 도메인ID를 기반으로 domain token을 생성하고 사용자 정보를 최신화하는 가드입니다.
 */
export const generateDomainTokenGuard: Guard<ServiceAppState> = async ({
  queryClient,
  state,
  context,
}) => {
  const { scope, token, domainId } = state;

  // domain scope 서비스가 아니라면 필요없는 로직이므로 바로 가드 종료
  if (scope !== TOKEN_SCOPE.DOMAIN) return { status: STATUS_CODE.OK };

  const clientIp = getClientIp(context);
  const { fetchCloudUserInfo } = iamApiInstance({ token });

  try {
    const { authToken, user, project } = await queryClient.fetchQuery({
      queryKey: [HOST_QUERY_KEY.GET_IAM_ACCESS_TOKEN],
      queryFn: () => fetchCloudUserInfo({ domainId, clientIp }),
    });

    return {
      token: authToken,
      domainName: user.domain?.name || '',
      projectId: project?.id || '',
      projectName: project?.name || '',
      defaultProjectId: user.default_project_id,
      status: STATUS_CODE.OK,
      userId: user.id,
      userName: user.name,
    };
  } catch (error) {
    const { status } = error as AxiosError;

    if (status === STATUS_CODE.FORBIDDEN) {
      // 현재 가지고 있는 토큰은 유효하지만 새로운 토큰(domain)을 발급하는 데 실패한 경우 (권한이 없거나, 잘못된 정보로 접근했거나 등)
      // 도메인 권한 획득 실패의 경우, 어떠한 도메인 권한도 없는 경우이기 때문에 PageRoleBoundary 에서 권한 갯수에 따라 처리하도록 하고 다음 실행
      // (토큰 api 의 결과 기반의 상태값을 통한 에러 템플릿 노출은 라우트 타입 가드가 동작하는 LNB 등을 눌러 이동 시에 200으로 초기화 되기 때문에 api 호출하는 것이 아니라면 상태를 유지시켜줄 수 없음.)
      return { status: STATUS_CODE.OK };
    } else {
      return commonGuardErrorHandler({ error, context, state });
    }
  }
};

generateDomainTokenGuard.displayName = 'generateDomainTokenGuard';
generateDomainTokenGuard.traceCategory = 'authentication';
